import React, { useEffect } from 'react';
import { LiveShowHero } from './LiveShowHero';
import { useCMS } from '../contexts/CMSContext';
import { siteMetadata } from '../config/siteMetadata';
import { renderStructuredDataScript } from '../utils/structuredData';

// Import clean content-only components for minimalistic approach
import UpNextSectionContent from './content/UpNextSectionContent';
import RecentShowsWidgetContent from './content/RecentShowsWidgetContent';
import UpcomingEventsWidgetContent from './content/UpcomingEventsWidgetContent';
import LatestBlogWidgetContent from './content/LatestBlogWidgetContent';
import FeaturedGalleryWidgetContent from './content/FeaturedGalleryWidgetContent';
import PresentersWidgetContent from './content/PresentersWidgetContent';

/**
 * HomePage - Minimalistic Flat Design (Based on HomePageC)
 *
 * Visual Hierarchy:
 * 1. Hero (LiveShowHero) - UNCHANGED - Most prominent
 * 2. All other sections - Minimal/clean styling (no backgrounds)
 *
 * This approach removes all backgrounds and borders from non-hero sections,
 * creating a very clean, minimal design where the hero stands out dramatically.
 * Content is organized purely through typography and spacing with subtle borders.
 */
const HomePage: React.FC = () => {
  const { settings } = useCMS();

  // Track that user visited home page for back button detection
  useEffect(() => {
    sessionStorage.setItem('lastVisitedPage', '/');
  }, []);

  const sections = [
    <UpNextSectionContent key="up-next" />,
    <PresentersWidgetContent key="presenters" />,
    <RecentShowsWidgetContent key="recent-shows" daysToShow={settings.recentShowsDays} showsPerDay={6} />,
  ];

  if (settings.showUpcomingEvents) {
    sections.push(
      <UpcomingEventsWidgetContent key="upcoming-events" count={3} showFeaturedOnly={false} />
    );
  }

  if (settings.showLatestBlog) {
    sections.push(
      <LatestBlogWidgetContent key="latest-blog" count={settings.blogPostCount} showFeaturedOnly={false} />
    );
  }

  if (settings.showFeaturedGallery) {
    sections.push(<FeaturedGalleryWidgetContent key="featured-gallery" />);
  }

  const homepageStructuredData = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "WebSite",
        "@id": `${siteMetadata.siteUrl}/#website`,
        url: `${siteMetadata.siteUrl}/`,
        name: siteMetadata.siteName,
        description: siteMetadata.defaultDescription,
        publisher: { "@id": `${siteMetadata.siteUrl}/#organization` },
      },
      {
        "@type": "Organization",
        "@id": `${siteMetadata.siteUrl}/#organization`,
        name: siteMetadata.siteName,
        url: `${siteMetadata.siteUrl}/`,
        logo: {
          "@type": "ImageObject",
          url: siteMetadata.defaultSocialImage,
        },
      },
      {
        "@type": "RadioStation",
        "@id": `${siteMetadata.siteUrl}/#station`,
        name: siteMetadata.siteName,
        url: `${siteMetadata.siteUrl}/`,
        description: siteMetadata.defaultDescription,
        logo: siteMetadata.defaultSocialImage,
        sameAs: [
          `https://twitter.com/${siteMetadata.twitterHandle.replace(/^@/, '')}`,
        ],
      },
    ],
  };

  const structuredDataScript = renderStructuredDataScript(
    'homepage-structured-data',
    homepageStructuredData
  );

  return (
    <>
      {structuredDataScript}
      <LiveShowHero />
      {sections.map((section, index) => (
        <MinimalSection key={index} isLast={index === sections.length - 1}>
          {section}
        </MinimalSection>
      ))}
    </>
  );
};

// Minimal section wrapper to keep spacing consistent across widgets
const MinimalSection: React.FC<{ children: React.ReactNode; isLast?: boolean }> = ({ children, isLast = false }) => (
  <section className={`w-full ${isLast ? '' : 'mb-8 sm:mb-12'}`}>
    <div className={isLast ? '' : 'mb-6 sm:mb-8'}>{children}</div>
  </section>
);

export default HomePage;
